import React, { useState, useRef, forwardRef, useImperativeHandle } from "react";
import TransactionGroup from "./TransactionGroup";
import Notifier from "../../components/Notifier";
import { orderBy, filter } from "lodash";
import { groupTransactionsByCategory } from "./transactionGroupingUtils";

const DesktopTransactionApprovalItems = forwardRef(({ selectionMode, onSelectionCountChange }, ref) => {
    const [transactions, setTransactions] = useState([]);
    const [currentTransactions, setCurrentTransactions] = useState([]);
    const [approvalMode, setApprovalMode] = useState(false); // Based on usage in the component
    const [selectedTransactions, setSelectedTransactions] = useState(new Set());
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const saveNotificationRef = useRef(null);

    const categoryLookup = useRef(null);
    const tagLookup = useRef(null);

    const toggleGroupCollapse = (groupName) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupName)) {
                newSet.delete(groupName);
            } else {
                newSet.add(groupName);
            }
            return newSet;
        });
    };

    // Check if a group is collapsed (default is collapsed unless explicitly expanded)
    const isGroupCollapsed = (groupName) => {
        return !expandedGroups.has(groupName);
    };


    useImperativeHandle(ref, () => ({
        setLookups(lookups) {
            categoryLookup.current = lookups.categories;
            tagLookup.current = lookups.tags;
        },
        setItems(items) {
            setTransactions(items);
            setCurrentTransactions(items);
            // Clear selections when new items are loaded
            setSelectedTransactions(new Set());
        },
        sortByPredictionGradeAndCategory() {
            setCurrentTransactions(orderBy(transactions, ["predictionGrade", "categoryId"], ["desc"]));
        },
        sortByDate() {
            setCurrentTransactions(orderBy(transactions, ["time"], ["asc"]));
        },
        filterByText(searchText) {
            setCurrentTransactions(
                filter(transactions, (item) =>
                    searchText ? item.description.includes(searchText) : true
                )
            );
        },
        getSelectedTransactions() {
            return transactions.filter(transaction => selectedTransactions.has(transaction.id));
        },
        getSelectedTransactionIds() {
            return Array.from(selectedTransactions);
        },
        clearSelection() {
            setSelectedTransactions(new Set());
        },
        selectAll() {
            setSelectedTransactions(new Set(currentTransactions.map(transaction => transaction.id)));
        },
        removeTransaction(id) {
            const newTransactions = transactions.filter((item) => item.id !== id);
            const newCurrentTransactions = currentTransactions.filter((item) => item.id !== id);
            setTransactions(newTransactions);
            setCurrentTransactions(newCurrentTransactions);
        },
        showBulkSaveSuccess(count) {
            saveNotificationRef.current?.showSuccess(`${count} עסקאות אושרו בהצלחה`);
        },
        showBulkSaveError(count) {
            saveNotificationRef.current?.showError(`שגיאה באישור ${count} עסקאות`);
        }
    }));

    const handleTransactionSelection = (transactionId, isSelected) => {
        setSelectedTransactions(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (isSelected) {
                newSelected.add(transactionId);
            } else {
                newSelected.delete(transactionId);
            }
            
            // Notify parent component of selection count change
            if (onSelectionCountChange) {
                onSelectionCountChange(newSelected.size);
            }
            
            return newSelected;
        });
    };

    const showSaveError = () => {
        saveNotificationRef.current?.showError();
    };

    const showDeleteError = () => {
        saveNotificationRef.current?.showError("שגיאה במחיקה");
    };

    const showSaveSuccess = () => {
        saveNotificationRef.current?.showSuccess();
    };

    const showDeleteSuccess = () => {
        saveNotificationRef.current?.showSuccess("נמחק בהצלחה");
    };

    const removeTransactionCallback = (id) => {
        const newTransactions = transactions.filter((item) => item.id !== id);
        const newCurrentTransactions = currentTransactions.filter((item) => item.id !== id);
        setTransactions(newTransactions);
        setCurrentTransactions(newCurrentTransactions);
    };

    const cardStyle = {
        background: "white",
        boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
        borderRadius: "10px",
    };

    const { paymentAppTransactions, categoryGroups } = groupTransactionsByCategory(
        currentTransactions,
        categoryLookup.current
    );

    return currentTransactions.length ? (
        <div
            style={{
                ...cardStyle,
                width: "65%",
                margin: "auto",
            }}
        >
            {/* Payment App Transactions Group */}
            {paymentAppTransactions.length > 0 && (
                <TransactionGroup
                    groupKey="paymentApps"
                    title="עסקאות BIT/PAYBOX"
                    transactions={paymentAppTransactions}
                    totalAmount={paymentAppTransactions.reduce((sum, t) => sum + (t.amount || 0), 0)}
                    isCollapsed={isGroupCollapsed('paymentApps')}
                    onToggleCollapse={toggleGroupCollapse}
                    isSpecial={true}
                    categoryLookup={categoryLookup.current}
                    tagLookup={tagLookup.current}
                    approvalMode={approvalMode}
                    removeTransactionCallback={removeTransactionCallback}
                    showSaveError={showSaveError}
                    showSaveSuccess={showSaveSuccess}
                    showDeleteError={showDeleteError}
                    showDeleteSuccess={showDeleteSuccess}
                    selectedTransactions={selectedTransactions}
                    onSelectionChange={handleTransactionSelection}
                    selectionMode={selectionMode}
                />
            )}

            {/* Category Groups */}
            {categoryGroups.map((group) => (
                <TransactionGroup
                    key={`category-${group.categoryId}`}
                    groupKey={`category-${group.categoryId}`}
                    title={group.categoryName}
                    transactions={group.transactions}
                    totalAmount={group.totalAmount}
                    isCollapsed={isGroupCollapsed(`category-${group.categoryId}`)}
                    onToggleCollapse={toggleGroupCollapse}
                    isSpecial={false}
                    categoryLookup={categoryLookup.current}
                    tagLookup={tagLookup.current}
                    approvalMode={approvalMode}
                    removeTransactionCallback={removeTransactionCallback}
                    showSaveError={showSaveError}
                    showSaveSuccess={showSaveSuccess}
                    showDeleteError={showDeleteError}
                    showDeleteSuccess={showDeleteSuccess}
                    selectedTransactions={selectedTransactions}
                    onSelectionChange={handleTransactionSelection}
                    selectionMode={selectionMode}
                />
            ))}

            <Notifier ref={saveNotificationRef} />
        </div>
    ) : (
        <h4 style={{ textAlign: "center" }}>לא נמצאו תוצאות</h4>
    );
});

export default DesktopTransactionApprovalItems;